import * as React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { Button, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';

import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

// Import track configuration functions
import { getAvailableTrackConfigs } from '../session-viewer/getSegmentDefinition';

// GraphQL imports
import { API } from 'aws-amplify';
import { createSessionList, getEventList } from '../../graphql/mutations';

export default function CreateNewSession({ defaultEventId, onSessionCreated }) {
  const [formData, setFormData] = React.useState({
    eventId: defaultEventId || '', // Pre-populate if provided
  });
  const [startDate, setStartDate] = React.useState(null);
  const [startTime, setStartTime] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [endTime, setEndTime] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  
  // Track configuration state
  const [selectedTrackConfig, setSelectedTrackConfig] = React.useState('');
  const [availableTrackConfigs, setAvailableTrackConfigs] = React.useState([]);
  const [filteredTrackConfigs, setFilteredTrackConfigs] = React.useState([]);
  const [eventTrackName, setEventTrackName] = React.useState('');

  console.log('Form data:', formData);

  // Load track configuration data on component mount
  React.useEffect(() => {
    try {
      const configs = getAvailableTrackConfigs();
      setAvailableTrackConfigs(configs);
    } catch (error) {
      console.error('Error loading track configuration data:', error);
    }
  }, []);

  // Load event details when eventId changes to get the track name
  React.useEffect(() => {
    if (formData.eventId) {
      loadEventDetails(formData.eventId);
    }
  }, [formData.eventId]);

  // Filter track configurations based on event's track
  React.useEffect(() => {
    if (eventTrackName && availableTrackConfigs.length > 0) {
      const configs = availableTrackConfigs.filter(config => 
        config.trackName === eventTrackName
      );
      setFilteredTrackConfigs(configs);
      setSelectedTrackConfig(''); // Reset selection when filtering changes
    } else {
      setFilteredTrackConfigs([]);
      setSelectedTrackConfig('');
    }
  }, [eventTrackName, availableTrackConfigs]);

  const loadEventDetails = async (eventId) => {
    try {
      // Get event details to find the track name
      const result = await API.graphql({
        query: `
          query GetEventList($id: ID!) {
            getEventList(id: $id) {
              id
              eventVenue
              eventName
            }
          }
        `,
        variables: { id: eventId }
      });
      
      if (result.data.getEventList?.eventVenue) {
        setEventTrackName(result.data.getEventList.eventVenue);
      }
    } catch (error) {
      console.error('Error loading event details:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const combineDateTime = (date, time) => {
    if (!date || !time) return null;
    
    // Combine date and time into a single dayjs object
    const combined = dayjs(date)
      .hour(time.hour())
      .minute(time.minute())
      .second(0)
      .millisecond(0);
    
    return combined.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.sessionName || !formData.eventId) {
      alert('Please fill in all required fields (Session Name and Event ID)');
      return;
    }

    if (!selectedTrackConfig) {
      alert('Please select a track configuration');
      return;
    }

    if (!startDate || !startTime) {
      alert('Please set the session start date and time');
      return;
    }

    setIsLoading(true);

    try {
      // Combine date and time for start and end
      const sessionStart = combineDateTime(startDate, startTime);
      const sessionEnd = combineDateTime(endDate, endTime);

      // Prepare the input for the GraphQL mutation
      const sessionInput = {
        sessionName: formData.sessionName,
        eventId: formData.eventId,
        trackConfig: selectedTrackConfig, // Use the selected track configuration ID
        sessionStart: sessionStart,
        sessionEnd: sessionEnd,
        // Note: we're not setting id - let DynamoDB auto-generate the id
      };

      console.log('Creating session with data:', sessionInput);

      // Call the GraphQL mutation
      const result = await API.graphql({
        query: createSessionList,
        variables: {
          input: sessionInput
        }
      });

      console.log('Session created successfully:', result);
      
      // Reset form after successful creation
      setFormData({ eventId: defaultEventId || '' }); // Keep eventId if provided
      setSelectedTrackConfig('');
      setStartDate(null);
      setStartTime(null);
      setEndDate(null);
      setEndTime(null);
      
      alert('Session created successfully!');
      
      // Call the callback to notify parent component
      if (onSessionCreated) {
        onSessionCreated(result);
      }
      
    } catch (error) {
      console.error('Error creating session:', error);
      alert('Error creating session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Typography variant="h6" gutterBottom>
        Create New Session
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              required
              id="sessionName"
              name="sessionName"
              label="Session Name"
              fullWidth
              variant="standard"
              value={formData.sessionName || ''}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="standard" required disabled={!eventTrackName}>
              <InputLabel>Select Track Configuration</InputLabel>
              <Select
                value={selectedTrackConfig}
                label="Select Track Configuration"
                onChange={(e) => setSelectedTrackConfig(e.target.value)}
              >
                {filteredTrackConfigs.map((config) => (
                  <MenuItem key={config.configId} value={config.configId}>
                    {config.configName} ({config.length} miles)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {eventTrackName && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Showing configurations for: {eventTrackName}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* Empty space for layout balance */}
          </Grid>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Session Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TimePicker 
                label="Session Start Time" 
                value={startTime}
                onChange={(newValue) => setStartTime(newValue)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Session End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TimePicker 
                label="Session End Time" 
                value={endTime}
                onChange={(newValue) => setEndTime(newValue)}
              />
            </Grid>
          </LocalizationProvider>

          <Grid item xs={12}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              size="large"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Session...' : 'Create Session'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </React.Fragment>
  );
} 